import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useAuth } from "../../context/AuthContext";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUser, faLock, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { getInformation } from "../../services/api";
import { Information } from "../../types/types";
import { resolveMediaUrl } from "../../utils/media";

interface LoginFormData {
  username: string;
  password: string;
}

const Login: React.FC = () => {
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [information, setInformation] = useState<Information | null>(null);
  const [logoLoading, setLogoLoading] = useState(true);

  useEffect(() => {
    const fetchInformation = async () => {
      try {
        const response = await getInformation();
        if (response.data && response.data.length > 0) {
          setInformation(response.data[0]);
        }
      } catch (error) {
        console.error("Error fetching information:", error);
      } finally {
        setLogoLoading(false);
      }
    };
    fetchInformation();
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/admin/dashboard");
    }
  }, [isAuthenticated, navigate]);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>();

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    try {
      await login(data.username, data.password);
      navigate("/admin/dashboard");
      toast.success("تم تسجيل الدخول بنجاح");
    } catch (error) {
      toast.error("فشل تسجيل الدخول. الرجاء التحقق من البيانات المدخلة");
      console.error("Login error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-r from-mauritania-green-dark to-mauritania-green flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-lg shadow-lg overflow-hidden">
        <div className="bg-mauritania-green-dark py-6 px-8 text-center flex flex-col items-center">
          {logoLoading ? (
            <div className="h-20 w-20 rounded-full bg-white/10 flex items-center justify-center mb-4">
              <FontAwesomeIcon icon={faSpinner} className="animate-spin text-white text-2xl" />
            </div>
          ) : (
            <img
              src={
                information?.logo
                  ? resolveMediaUrl(information.logo)
                  : "https://via.placeholder.com/80x80/00a95c/FFFFFF?text=MO"
              }
              alt="Ministry Logo"
              className="h-20 w-20 rounded-full object-cover border-2 border-white/30 shadow-md mb-4 bg-white"
            />
          )}
          <h2 className="text-2xl font-bold text-white">تسجيل الدخول</h2>
          <p className="text-mauritania-gold mt-2">
            لوحة تحكم وزارة العقارات وأملاك الدولة
          </p>
        </div>
        <div className="p-8">
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="mb-6">
              <label htmlFor="username" className="block text-gray-700 mb-2">
                اسم المستخدم
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <FontAwesomeIcon icon={faUser} className="text-gray-400" />
                </div>
                <input
                  type="text"
                  id="username"
                  {...register("username", { required: "اسم المستخدم مطلوب" })}
                  className="w-full pr-10 pl-3 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-mauritania-green focus:border-transparent"
                  placeholder="أدخل اسم المستخدم"
                />
              </div>
              {errors.username && (
                <p className="mt-1 text-red-500 text-sm">
                  {errors.username.message}
                </p>
              )}
            </div>
            <div className="mb-6">
              <label htmlFor="password" className="block text-gray-700 mb-2">
                كلمة المرور
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <FontAwesomeIcon icon={faLock} className="text-gray-400" />
                </div>
                <input
                  type="password"
                  id="password"
                  {...register("password", { required: "كلمة المرور مطلوبة" })}
                  className="w-full pr-10 pl-3 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-mauritania-green focus:border-transparent"
                  placeholder="أدخل كلمة المرور"
                />
              </div>
              {errors.password && (
                <p className="mt-1 text-red-500 text-sm">
                  {errors.password.message}
                </p>
              )}
            </div>
            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-mauritania-green text-white py-3 px-4 rounded-md hover:bg-mauritania-green-dark transition duration-300 focus:outline-none focus:ring-2 focus:ring-mauritania-green focus:ring-opacity-50"
            >
              {isLoading ? "جاري تسجيل الدخول..." : "تسجيل الدخول"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
